import { Menu, Transition } from '@headlessui/react';
import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import { FC, Fragment, ReactNode } from 'react';
import {
  FiBold,
  FiCode,
  FiItalic,
  FiList,
  FiMessageSquare,
  FiPlus,
  FiRotateCcw,
  FiRotateCw,
} from 'react-icons/fi';
import { GoListOrdered } from 'react-icons/go';
import { MdHorizontalRule, MdStrikethroughS } from 'react-icons/md';
import { twMerge } from 'tailwind-merge';

import { menuBarStateSelector } from './menubar-action';

type ToolbarIconButtonProps = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isActive?: boolean;
  icon: ReactNode;
};

const ToolbarIconButton: FC<ToolbarIconButtonProps> = ({ label, onClick, disabled, isActive, icon }) => (
  <button
    type='button'
    className={twMerge('tiptap-toolbar-btn', isActive && 'is-active')}
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    aria-pressed={isActive}
  >
    {icon}
  </button>
);

type ToolbarLabelButtonProps = {
  label: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  isActive?: boolean;
};

const ToolbarLabelButton: FC<ToolbarLabelButtonProps> = ({ label, title, onClick, disabled, isActive }) => (
  <button
    type='button'
    className={twMerge('tiptap-toolbar-btn tiptap-toolbar-btn--label', isActive && 'is-active')}
    onClick={onClick}
    disabled={disabled}
    aria-label={title}
    title={title}
    aria-pressed={isActive}
  >
    {label}
  </button>
);

type OverflowMenuItemProps = {
  label: string;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
};

const OverflowMenuItem: FC<OverflowMenuItemProps> = ({ label, onClick, isActive, disabled: itemDisabled }) => (
  <Menu.Item disabled={itemDisabled} as='div' className='px-1'>
    {({ active, disabled }) => (
      <button
        type='button'
        onClick={onClick}
        disabled={disabled}
        className={twMerge(
          'tiptap-overflow-item',
          (active || isActive) && 'is-active',
          disabled && 'is-disabled'
        )}
      >
        {label}
      </button>
    )}
  </Menu.Item>
);

type TipTapMenuBarProps = {
  editor: Editor | null;
  disabled?: boolean;
};

export const TipTapMenuBar = ({ editor, disabled = false }: TipTapMenuBarProps) => {
  const editorState = useEditorState({
    editor,
    selector: menuBarStateSelector,
  });

  if (!editor) {
    return null;
  }

  const run = (command: () => void) => {
    if (disabled) return;
    command();
  };

  return (
    <div className='tiptap-composer__toolbar tiptap-menu-bar-control-group' role='toolbar' aria-label='Formatting'>
      <div className='flex flex-wrap items-center gap-0.5 px-2 py-1.5'>
        <ToolbarIconButton
          label='Bold'
          icon={<FiBold className='size-4' />}
          onClick={() => run(() => editor.chain().focus().toggleBold().run())}
          disabled={disabled || !editorState.canBold}
          isActive={editorState.isBold}
        />
        <ToolbarIconButton
          label='Italic'
          icon={<FiItalic className='size-4' />}
          onClick={() => run(() => editor.chain().focus().toggleItalic().run())}
          disabled={disabled || !editorState.canItalic}
          isActive={editorState.isItalic}
        />
        <ToolbarIconButton
          label='Strikethrough'
          icon={<MdStrikethroughS className='size-4' />}
          onClick={() => run(() => editor.chain().focus().toggleStrike().run())}
          disabled={disabled || !editorState.canStrike}
          isActive={editorState.isStrike}
        />
        <ToolbarLabelButton
          label='P'
          title='Paragraph'
          onClick={() => run(() => editor.chain().focus().setParagraph().run())}
          disabled={disabled}
          isActive={editorState.isParagraph}
        />
        <ToolbarLabelButton
          label='H1'
          title='Heading 1'
          onClick={() => run(() => editor.chain().focus().toggleHeading({ level: 1 }).run())}
          disabled={disabled}
          isActive={editorState.isHeading1}
        />
        <ToolbarLabelButton
          label='H2'
          title='Heading 2'
          onClick={() => run(() => editor.chain().focus().toggleHeading({ level: 2 }).run())}
          disabled={disabled}
          isActive={editorState.isHeading2}
        />
        <ToolbarIconButton
          label='Bullet list'
          icon={<FiList className='size-4' />}
          onClick={() => run(() => editor.chain().focus().toggleBulletList().run())}
          disabled={disabled}
          isActive={editorState.isBulletList}
        />
        <ToolbarIconButton
          label='Ordered list'
          icon={<GoListOrdered className='size-4' />}
          onClick={() => run(() => editor.chain().focus().toggleOrderedList().run())}
          disabled={disabled}
          isActive={editorState.isOrderedList}
        />
        <ToolbarIconButton
          label='Blockquote'
          icon={<FiMessageSquare className='size-4' />}
          onClick={() => run(() => editor.chain().focus().toggleBlockquote().run())}
          disabled={disabled}
          isActive={editorState.isBlockquote}
        />
        <ToolbarIconButton
          label='Inline code'
          icon={<FiCode className='size-4' />}
          onClick={() => run(() => editor.chain().focus().toggleCode().run())}
          disabled={disabled || !editorState.canCode}
          isActive={editorState.isCode}
        />
        <ToolbarIconButton
          label='Horizontal rule'
          icon={<MdHorizontalRule className='size-4' />}
          onClick={() => run(() => editor.chain().focus().setHorizontalRule().run())}
          disabled={disabled}
        />
        <ToolbarIconButton
          label='Undo'
          icon={<FiRotateCcw className='size-4' />}
          onClick={() => run(() => editor.chain().focus().undo().run())}
          disabled={disabled || !editorState.canUndo}
        />
        <ToolbarIconButton
          label='Redo'
          icon={<FiRotateCw className='size-4' />}
          onClick={() => run(() => editor.chain().focus().redo().run())}
          disabled={disabled || !editorState.canRedo}
        />

        <Menu as='div' className='relative'>
          {({ open }) => (
            <>
              <Menu.Button
                type='button'
                className={twMerge('tiptap-toolbar-btn', open && 'is-active')}
                aria-label='More formatting'
                title='More formatting'
                disabled={disabled}
              >
                <FiPlus className='size-4' />
              </Menu.Button>
              <Transition
                show={open}
                as={Fragment}
                enter='transition ease-out duration-100'
                enterFrom='transform opacity-0 scale-95'
                enterTo='transform opacity-100 scale-100'
                leave='transition ease-in duration-75'
                leaveFrom='transform opacity-100 scale-100'
                leaveTo='transform opacity-0 scale-95'
              >
                <Menu.Items className='tiptap-overflow-menu absolute bottom-full left-0 z-50 mb-1 origin-bottom-left focus:outline-none'>
                  <OverflowMenuItem
                    label='Heading 3'
                    onClick={() => run(() => editor.chain().focus().toggleHeading({ level: 3 }).run())}
                    isActive={editorState.isHeading3}
                  />
                  <OverflowMenuItem
                    label='Heading 4'
                    onClick={() => run(() => editor.chain().focus().toggleHeading({ level: 4 }).run())}
                    isActive={editorState.isHeading4}
                  />
                  <OverflowMenuItem
                    label='Heading 5'
                    onClick={() => run(() => editor.chain().focus().toggleHeading({ level: 5 }).run())}
                    isActive={editorState.isHeading5}
                  />
                  <OverflowMenuItem
                    label='Heading 6'
                    onClick={() => run(() => editor.chain().focus().toggleHeading({ level: 6 }).run())}
                    isActive={editorState.isHeading6}
                  />
                  <OverflowMenuItem
                    label='Code block'
                    onClick={() => run(() => editor.chain().focus().toggleCodeBlock().run())}
                    isActive={editorState.isCodeBlock}
                  />
                  <OverflowMenuItem label='Hard break' onClick={() => run(() => editor.chain().focus().setHardBreak().run())} />
                  <OverflowMenuItem
                    label='Clear marks'
                    onClick={() => run(() => editor.chain().focus().unsetAllMarks().run())}
                    disabled={!editorState.canClearMarks}
                  />
                  <OverflowMenuItem label='Clear nodes' onClick={() => run(() => editor.chain().focus().clearNodes().run())} />
                </Menu.Items>
              </Transition>
            </>
          )}
        </Menu>
      </div>
    </div>
  );
};
